import close_icon from "../../../../assets/icons/close.svg";
import perfil_icon from "../../../../assets/perfil.png";
import pin_icon from "../../../../assets/icons/pin.svg";
import { tiposGerenciamentoModal } from "../../../../utils/data";
import { Main } from "../styled";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Loading from "../../../components/Loading";
import Esqueleto, { Recarregando } from "../../../components/Esqueleto";
import { Content } from "./styled";
import TabelaAdmin from "../../../components/TabelaAdmin";
import Paginacao from "../../../components/Paginacao";
import CircularImage from "../../../components/CircularImage";
import InputSearch from "../../../components/InputSearch";
import DeleteDialog from "../../../components/DeleteDialog";
import Usuario from "../../../../models/Usuario";
import { mensagemDeErro, respostaOk } from "../../../../utils/resposta";

const POR_PAGINA_INICIAL = 20;

// O status "Aguardando Confirmação" tem espaço no nome; a classe CSS não.
const classeStatus = (status) => (status || '').split(' ')[0];

// Cada ação vira um texto de confirmação: o que acontece e com quem.
function descreverAcao(usuario, acao) {
  const nome = usuario.nome;
  switch (acao) {
    case 'promover':
      return {
        titulo: `Promover ${nome} a Profissional?`,
        subtitulo: 'A pessoa passa a poder registrar áreas de plantio (várias coordenadas e espécies).',
        botao: 'Promover',
      };
    case 'promoverAdmin':
      return {
        titulo: `Promover ${nome} a Administrador?`,
        subtitulo: 'A pessoa passa a ter acesso total ao Painel administrativo: usuários, pedidos, grupos, conteúdo e relatórios. Faça isso só para quem você confia.',
        botao: 'Promover a Administrador',
      };
    case 'rebaixar':
      return {
        titulo: `Rebaixar ${nome} a Comum?`,
        subtitulo: 'A pessoa deixa de poder criar áreas novas. As áreas que já registrou continuam disponíveis para edição.',
        botao: 'Rebaixar',
      };
    case 'desativar':
      return {
        titulo: `Desativar a conta de ${nome}?`,
        subtitulo: 'A pessoa perde o acesso na hora, inclusive se estiver logada, e não consegue entrar de novo até ser reativada.',
        botao: 'Desativar',
      };
    default:
      return {
        titulo: `Ativar a conta de ${nome}?`,
        subtitulo: 'A pessoa volta a poder entrar na plataforma.',
        botao: 'Ativar',
      };
  }
}

function mensagemSucesso(usuario, acao) {
  switch (acao) {
    case 'promover': return `${usuario.nome} agora é Profissional.`;
    case 'promoverAdmin': return `${usuario.nome} agora é Administrador.`;
    case 'rebaixar': return `${usuario.nome} voltou a ser Comum.`;
    case 'desativar': return `A conta de ${usuario.nome} foi desativada.`;
    default: return `A conta de ${usuario.nome} foi ativada.`;
  }
}

function aplicarAcao(acao) {
  switch (acao) {
    case 'promover': return (id) => Usuario.alterarTipo(id, 'Profissional');
    case 'promoverAdmin': return (id) => Usuario.alterarTipo(id, 'Administrador');
    case 'rebaixar': return (id) => Usuario.alterarTipo(id, 'Comum');
    case 'desativar': return (id) => Usuario.alterarStatus(id, 'Inativo');
    default: return (id) => Usuario.alterarStatus(id, 'Ativo');
  }
}

// Campo do usuário que cada ação altera, e o novo valor, para atualizar a linha
// sem precisar buscar a lista de novo.
const efeitoLocal = {
  promover: { tipo: 'Profissional' },
  promoverAdmin: { tipo: 'Administrador' },
  rebaixar: { tipo: 'Comum' },
  desativar: { status: 'Inativo' },
  ativar: { status: 'Ativo' },
};

function ListaUsuariosModal({ tipo, onClose, setUsuario, openEditModal, pagina = false, versao = 0 }) {
  const logado = useSelector((state) => state.usuario);
  const aberto = pagina || tipo !== tiposGerenciamentoModal.NONE;

  const [usuarios, setUsuarios] = useState({ count: 0, rows: [] });
  const [page, setPage] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_INICIAL);
  const topo = useRef(null);
  const [busca, setBusca] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");
  // `loading` = a LISTA carregando (esqueleto); `agindo` = bloquear/promover (overlay: grava e bloqueia).
  const [loading, setLoading] = useState(false);
  const [agindo, setAgindo] = useState(false);
  const [erro, setErro] = useState("");
  const [feedback, setFeedback] = useState("");
  const [confirmacao, setConfirmacao] = useState(null); // { usuario, acao }
  const [erroConfirmacao, setErroConfirmacao] = useState("");
  const [typingTimeout, setTypingTimeout] = useState();
  // Descarta a resposta de uma consulta velha quando o filtro mudou no meio.
  const requisicao = useRef(0);

  const buscar = useCallback(async () => {
    const atual = ++requisicao.current;
    setLoading(true);
    const resultado = await Usuario.findAdminLista(page, porPagina, { busca, tipo: filtroTipo, status: filtroStatus });
    if (atual !== requisicao.current) return;

    if (respostaOk(resultado)) {
      const { count, rows } = resultado.data;
      // Página que deixou de existir: vai para a última que existe.
      if (rows.length === 0 && count > 0 && page > 1) {
        setPage(Math.ceil(count / porPagina));
        return;
      }
      setErro("");
      setUsuarios(resultado.data);
    } else {
      setErro(mensagemDeErro(resultado));
    }
    setLoading(false);
  }, [page, porPagina, busca, filtroTipo, filtroStatus]);

  useEffect(() => {
    if (aberto) buscar();
  }, [aberto, buscar]);

  // `versao` sobe quando quem usa a lista mudou um usuário por fora (a edição
  // de dados é outro modal). Volta à primeira página: refazer só a página atual
  // duplicaria linhas, porque as páginas seguintes são ACRESCENTADAS à lista.
  const primeiraVersao = useRef(true);
  useEffect(() => {
    if (primeiraVersao.current) { primeiraVersao.current = false; return; }
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versao]);

  // Fechou: a próxima abertura começa limpa, sem o filtro nem a lista antigos.
  useEffect(() => {
    if (!aberto) {
      setUsuarios({ count: 0, rows: [] });
      setPage(1);
      setBusca("");
      setFiltroTipo("");
      setFiltroStatus("");
      setFeedback("");
      setErro("");
    }
  }, [aberto]);

  function mudarFiltro(setter) {
    return (e) => {
      setter(e.target.value);
      setPage(1);
    };
  }

  function handleSearch(e) {
    const valor = e.target.value;
    clearTimeout(typingTimeout);
    setTypingTimeout(setTimeout(() => {
      setBusca(valor);
      setPage(1);
    }, 500));
  }

  async function confirmar() {
    const { usuario, acao } = confirmacao;
    setErroConfirmacao("");
    setAgindo(true);
    const resultado = await aplicarAcao(acao)(usuario.id);
    setAgindo(false);

    if (!respostaOk(resultado)) {
      // 403 do backend, p.ex. ao agir sobre a própria conta: mostra a mensagem dele.
      setErroConfirmacao(mensagemDeErro(resultado));
      return;
    }

    const efeito = efeitoLocal[acao];
    setUsuarios((atual) => ({
      ...atual,
      rows: atual.rows.map((u) => (u.id === usuario.id ? { ...u, ...efeito } : u)),
    }));
    setFeedback(mensagemSucesso(usuario, acao));
    setConfirmacao(null);
  }

  if (!aberto) return null;

  const dialogo = confirmacao ? descreverAcao(confirmacao.usuario, confirmacao.acao) : null;

  // Mesmo conteúdo em dois formatos: modal (padrão, usado pelo Gerenciamento
  // antigo) e PÁGINA (`pagina`, ambiente /admin), sem overlay nem botão de fechar.
  const avisos = (
    <>
      {agindo && <Loading titulo={"Aguarde um momento"} />}

      {confirmacao &&
        <DeleteDialog
          error={erroConfirmacao}
          titulo={dialogo.titulo}
          subtitulo={dialogo.subtitulo}
          textButton={dialogo.botao}
          onDelete={confirmar}
          onClose={() => { setConfirmacao(null); setErroConfirmacao(""); }}
        />
      }
    </>
  );

  const colunas = [
    {
      id: "usuario", rotulo: "Usuário", largura: "minmax(220px, 2.2fr)",
      celula: (usuario) => (
        <div className="col-usuario">
          <CircularImage src={usuario.foto_perfil?.path || perfil_icon} alt="" />
          <div className="user-info">
            <div className="nome" title={usuario.nome}>{usuario.nome}{Number(usuario.id) === Number(logado?.id) && ' (você)'}</div>
            <div className="email" title={usuario.email}>{usuario.email}</div>
          </div>
        </div>
      ),
    },
    {
      id: "local", rotulo: "Localização", largura: "minmax(150px, 1.4fr)",
      celula: (usuario) => (
        <div className="location-info">
          <img src={pin_icon} alt="" />
          <span>{[usuario.cidade, usuario.estado].filter(Boolean).join(' / ') || 'Local não informado'}</span>
        </div>
      ),
    },
    {
      id: "perfil", rotulo: "Perfil", largura: "120px",
      celula: (usuario) => <span className={`selo tipo-${usuario.tipo}`}>{usuario.tipo}</span>,
    },
    {
      id: "status", rotulo: "Status", largura: "170px",
      celula: (usuario) => <span className={`selo status-${classeStatus(usuario.status)}`}>{usuario.status}</span>,
    },
    {
      id: "acoes", rotulo: "Ações", largura: "minmax(250px, auto)", direita: true,
      celula: (usuario) => {
        const ehOProprio = Number(usuario.id) === Number(logado?.id);
        return (
          <div className="acoes">
            <button type="button" className="secundario" onClick={() => { setUsuario(usuario); openEditModal(); }}>Editar</button>
            {ehOProprio
              ? <span className="voce">Você não pode alterar a própria conta.</span>
              : <>
                {usuario.tipo === 'Comum' &&
                  <button type="button" onClick={() => setConfirmacao({ usuario, acao: 'promover' })}>Promover a Profissional</button>
                }
                {usuario.tipo !== 'Administrador' &&
                  <button type="button" className="secundario" onClick={() => setConfirmacao({ usuario, acao: 'promoverAdmin' })}>Promover a Administrador</button>
                }
                {usuario.tipo !== 'Comum' &&
                  <button type="button" className="secundario" onClick={() => setConfirmacao({ usuario, acao: 'rebaixar' })}>Rebaixar a Comum</button>
                }
                {usuario.status === 'Ativo'
                  ? <button type="button" className="perigo" onClick={() => setConfirmacao({ usuario, acao: 'desativar' })}>Desativar</button>
                  : <button type="button" className="secundario" onClick={() => setConfirmacao({ usuario, acao: 'ativar' })}>Ativar</button>
                }
              </>
            }
          </div>
        );
      },
    },
  ];

  const corpo = (
    <Content>
      <InputSearch text="Buscar por nome ou e-mail" onChangeCapture={handleSearch} />

      <div className="filtros">
        <select aria-label="Filtrar por tipo" value={filtroTipo} onChange={mudarFiltro(setFiltroTipo)}>
          <option value="">Todos os tipos</option>
          <option value="Comum">Comum</option>
          <option value="Profissional">Profissional</option>
          <option value="Administrador">Administrador</option>
        </select>
        <select aria-label="Filtrar por status" value={filtroStatus} onChange={mudarFiltro(setFiltroStatus)}>
          <option value="">Todos os status</option>
          <option value="Ativo">Ativo</option>
          <option value="Inativo">Inativo</option>
          <option value="Aguardando Confirmação">Aguardando confirmação</option>
        </select>
      </div>

      <span ref={topo} />
      {feedback && <span className="feedback" role="status">{feedback}</span>}
      {erro && <span className="erro-lista" role="alert">{erro}</span>}

      {!loading && !erro && usuarios.rows.length === 0 &&
        <span className="vazio">Nenhum usuário encontrado com esses filtros.</span>
      }

      {loading && usuarios.rows.length === 0 && <Esqueleto variante="linha" quantidade={6} rotulo="Carregando usuários" />}
      <Recarregando $ativo={loading && usuarios.rows.length > 0} aria-busy={loading && usuarios.rows.length > 0}>
        <TabelaAdmin
          rotulo="Usuários da plataforma"
          linhas={usuarios.rows}
          chave={(u) => `usuario-${u.id}`}
          colunas={colunas}
        />
      </Recarregando>

      {!(loading && usuarios.rows.length === 0) &&
      <Paginacao
        pagina={page} total={usuarios.count} porPagina={porPagina} itens="usuários"
        onMudarPagina={(n) => { setPage(n); topo.current?.scrollIntoView({ block: "start", behavior: "smooth" }); }}
        onMudarPorPagina={(n) => { setPorPagina(n); setPage(1); }}
      />
      }
    </Content>
  );

  if (pagina) {
    return (
      <>
        {avisos}
        {corpo}
      </>
    );
  }

  return (
    <Main>
      {avisos}

      <div className="content-modal" style={{ width: 'min(560px, 94vw)', maxHeight: '86vh' }}>
        <div className="top">
          <h3>Usuarios</h3>
          <button type="button" onClick={onClose}>
            <img src={close_icon} alt="Icone de fechar" />
          </button>
        </div>
        {corpo}
      </div>
    </Main>
  );
}

export default ListaUsuariosModal;
